import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography, useTheme } from '@mui/material';
import ResponsiveDialog from 'components/dialogs/ResponsiveDialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Query, queryOps } from 'system/query/Query';
import { QueryInfo } from 'system/query/QueryInfo';
import { QuickStartTermSelectors } from './queryBuilder/common/QuickStartTermSelectors';
import { QuickStartTerm } from './queryBuilder/common/QuickStartTermSelector';
import { QuerySegment } from 'system/query/QuerySegment';
import { QueryBranch } from 'system/query/QueryBranch';
import { QueryLeafNode } from 'system/query/QueryLeafNode';

export interface QueryQuickStartDialogProps {
  queryInfo?: QueryInfo;
  onReplaceQuery: (query: Query) => void;
  onInsertQuery: (query: Query) => void;
  onClose: () => void;
}


export default function QueryQuickStartDialog(props: QueryQuickStartDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const { queryInfo, onReplaceQuery, onInsertQuery, onClose } = props;

  const [andTerms, setAndTerms] = React.useState<QuickStartTerm[]>([]);
  const [orTerms, setOrTerms] = React.useState<QuickStartTerm[]>([]);
  const [notTerms, setNotTerms] = React.useState<QuickStartTerm[]>([]);

  function buildQuery(): Query {
    const andTextTerms = andTerms.filter(term => !term.isEntity).map(term => {
      return {
        op: "contains",
        fields: ["title", "description", "body"],
        value: term.label
      } as QueryLeafNode;
    });
    const andEntityTerms = andTerms.filter(term => term.isEntity).map(term => {
      return {
        op: "contains",
        fields: ["entities"],
        value: term.id,
        label: term.label

      } as QueryLeafNode;
    });

    const orTextTerms = orTerms.filter(term => !term.isEntity).map(term => term.label);
    const orEntityTerms = orTerms.filter(term => term.isEntity);

    let orBlock: QueryBranch | undefined;
    if (orTextTerms.length || orEntityTerms.length) {
      orBlock = {
        op: "or",
        values: [] as QuerySegment[]
      } as QueryBranch;
      if (orTextTerms.length && orBlock.values) {
        orBlock.values.push({
          op: queryOps.contains,
          fields: ["title", "description", "body"],
          values: orTextTerms
        } as QueryLeafNode);
      }
      if (orEntityTerms.length && orBlock.values) {
        orBlock.values.push({
          op: queryOps.contains,
          fields: ["entities"],
          values: orEntityTerms.map(term => term.id),
          labels: orEntityTerms.map(term => term.label)
        } as QueryLeafNode);
      }
    }

    const notTextTerms = notTerms.filter(term => !term.isEntity).map(term => term.label);
    const notEntityTerms = notTerms.filter(term => term.isEntity);

    let notBlock: QueryBranch | undefined;
    if (notTextTerms.length || notEntityTerms.length) {
      notBlock = {
        op: "not",
        values: [] as QuerySegment[]
      } as QueryBranch;
      if (notTextTerms.length && notBlock.values) {
        notBlock.values.push({
          op: queryOps.contains,
          fields: ["title", "description", "body"],
          values: notTextTerms
        } as QueryLeafNode);
      }
      if (notEntityTerms.length && notBlock.values) {
        notBlock.values.push({
          op: queryOps.contains,
          fields: ["entities"],
          values: notEntityTerms.map(term => term.id),
          labels: notEntityTerms.map(term => term.label)
        } as QueryLeafNode);
      }
    }

    const newQuery = new Query({
      dataset_id: queryInfo?.dataset?.id,
      query: {
        op: "and",
        values: []
      }
    });
    const root = newQuery.query as QueryBranch;
    if (root.values && andTextTerms.length) {
      root.values.push(...andTextTerms);
    }
    if (root.values && andEntityTerms.length) {
      root.values.push(...andEntityTerms);
    }
    if (orBlock) {
      root.values?.push(orBlock);
    }
    if (notBlock) {
      root.values?.push(notBlock);
    }

    return newQuery;
  }

  return (
    <ResponsiveDialog
      open={true}
      disableClickaway={true}
      onClose={onClose}
      maxWidth="md"
    >
      <DialogTitle>{i18n.t("query-form.quick-start")}</DialogTitle>
      <DialogContent>
        <Typography variant="h4" sx={{mt:3, mb:1, fontWeight: "normal", fontSize: "1.4em"}}>
          <strong>{i18n.t("query-form.or-items")}</strong>
          <span style={{padding: "0 5px"}}>{i18n.t("query-form.or-items-description")}</span>
          <span style={{color: theme.colors.primary.main}}>{i18n.t("query-form.or-items-op")}</span>
        </Typography>
        <QuickStartTermSelectors
          terms={orTerms}
          onTermsChange={(terms) => setOrTerms(terms || [])}
        />
        <Typography variant="h4" sx={{mt:1, mb:1, fontWeight: "normal", fontSize: "1.4em"}}>
          <strong>{i18n.t("query-form.and-items")}</strong>
          <span style={{padding: "0 5px"}}>{i18n.t("query-form.and-items-description")}</span>
          <span style={{color: theme.colors.primary.main}}>{i18n.t("query-form.and-items-op")}</span>
        </Typography>
        <QuickStartTermSelectors
          terms={andTerms}
          onTermsChange={(terms) => setAndTerms(terms || [])}
        />
        <Typography variant="h4" sx={{mt:3, mb:1, fontWeight: "normal", fontSize: "1.4em"}}>
          <strong>{i18n.t("query-form.not-items")}</strong>
          <span style={{padding: "0 5px"}}>{i18n.t("query-form.not-items-description")}</span>
          <span style={{color: theme.colors.primary.main}}>{i18n.t("query-form.not-items-op")}</span>
        </Typography>
        <QuickStartTermSelectors
          terms={notTerms}
          onTermsChange={(terms) => setNotTerms(terms || [])}
        />
      </DialogContent>
      <DialogActions sx={{ padding: "0 18px 18px 0" }} >
        <Button
          autoFocus
          size="small"
          variant="contained"
          color="secondary"
          onClick={props.onClose} >
          { i18n.t("cancel") }
        </Button>
        <Button
          variant="contained"
          color='success'
          size="small"
          disabled={!andTerms.length && !orTerms.length && !notTerms.length}
          onClick={() => {
            onInsertQuery(buildQuery());
          }} >
          { i18n.t("insert") }
        </Button>
        <Button
          variant="contained"
          color='success'
          size="small"
          disabled={!andTerms.length && !orTerms.length && !notTerms.length}
          onClick={() => {
            onReplaceQuery(buildQuery());
          }} >
          { i18n.t("replace") }
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}